import { useEffect, useState } from "react";
import { Accessibility, Minus, Plus, RotateCcw, X } from "lucide-react";
import { useLanguage } from "../i18n";

const FONT_STEPS = [87.5, 100, 112.5, 125];
const MODES = ["normal", "night", "contrast", "gray", "sepia"];

function applyA11y(state) {
  const root = document.documentElement;
  root.style.fontSize = `${FONT_STEPS[state.font]}%`;
  root.classList.remove("a11y-night", "a11y-contrast", "a11y-gray", "a11y-sepia");
  if (state.mode !== "normal") root.classList.add(`a11y-${state.mode}`);
  root.classList.toggle("a11y-reduced", !!state.motion);
}

export default function AccessibilityWidget() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("atlas_a11y") || "null");
      if (saved) return { font: 1, mode: "normal", motion: false, ...saved };
    } catch { /* ignore */ }
    return {
      font: 1,
      mode: "normal",
      motion: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false,
    };
  });

  useEffect(() => {
    applyA11y(state);
    localStorage.setItem("atlas_a11y", JSON.stringify(state));
  }, [state]);

  const setFont = (f) => setState((s) => ({ ...s, font: Math.min(3, Math.max(0, f)) }));
  const reset = () => setState({ font: 1, mode: "normal", motion: false });

  const modeBtn = (m) =>
    `rounded-full px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] transition-colors ${
      state.mode === m
        ? "bg-[#1E293B] text-white"
        : "bg-white border border-slate-200 text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35]"
    }`;

  return (
    <>
      <button
        data-testid="a11y-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("a11y.open")}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-[#1E293B] text-white shadow-xl shadow-slate-300 flex items-center justify-center hover:bg-[#0F172A] hover:scale-105 transition-all"
      >
        {open ? <X className="w-6 h-6" /> : <Accessibility className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="a11y-panel"
          className="fixed bottom-24 left-6 z-50 w-[min(92vw,320px)] rounded-3xl bg-white border border-slate-200 shadow-2xl p-6">
          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">
            {t("a11y.open")}
          </div>

          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
            {t("a11y.font")}
          </div>
          <div className="flex items-center gap-3 mb-5">
            <button data-testid="a11y-font-dec" onClick={() => setFont(state.font - 1)} disabled={state.font === 0}
              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">
              <Minus className="w-4 h-4" />
            </button>
            <div className="flex-1 text-center font-display font-bold text-slate-900" data-testid="a11y-font-value">
              {FONT_STEPS[state.font]}%
            </div>
            <button data-testid="a11y-font-inc" onClick={() => setFont(state.font + 1)} disabled={state.font === 3}
              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">
              <Plus className="w-4 h-4" />
            </button>
          </div>

          <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">
            {t("a11y.colors")}
          </div>
          <div className="flex flex-wrap gap-2 mb-5">
            {MODES.map((m) => (
              <button key={m} data-testid={`a11y-mode-${m}`} onClick={() => setState((s) => ({ ...s, mode: m }))}
                className={modeBtn(m)}>
                {t(`a11y.${m}`)}
              </button>
            ))}
          </div>

          <button data-testid="a11y-motion" onClick={() => setState((s) => ({ ...s, motion: !s.motion }))}
            aria-pressed={state.motion}
            className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 mb-5 transition-colors ${
              state.motion ? "border-[#FF6B35] bg-orange-50" : "border-slate-200 hover:border-[#FF6B35]/50"
            }`}>
            <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              {t("a11y.motion")}
            </span>
            <span className={`relative w-9 h-5 rounded-full transition-colors ${state.motion ? "bg-[#FF6B35]" : "bg-slate-200"}`}>
              <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
                style={{ left: state.motion ? 18 : 2 }} />
            </span>
          </button>

          <button data-testid="a11y-reset" onClick={reset}
            className="inline-flex items-center gap-2 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400 hover:text-[#FF6B35] transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> {t("a11y.reset")}
          </button>
        </div>
      )}
    </>
  );
}
